import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import SearchMenuListItem from 'components/Search/SearchForm/SearchMenu/SearchMenuListItem'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import noop from 'lib/noop'
import {
  TRIP_PLACE_SEARCH_TYPES,
  TRIP_PLACE_TYPEAHEAD_TYPES,
} from 'tripPlanner/config/search'

interface Props {
  title?: string
  tripOrigin?: { id: string; name: string }
  onChange: (value: { id: string; name: string }) => void
  onRemove: (e: React.MouseEvent) => void
  onCancel: () => void
  onBack?: () => void
  isSubmitting?: boolean
  errorMsg?: string
  onRequestClearError?: () => void
}

const MobileSelectTripOrigin = ({
  title = 'Departure location',
  tripOrigin,
  onChange,
  onRemove,
  onCancel,
  onBack,
  isSubmitting,
  errorMsg: externalErrorMsg,
  onRequestClearError = noop,
}: Props) => {
  const [inputValue, setInputValue] = useState(tripOrigin?.name ?? '')

  const initialHasOrigin = useRef(!!tripOrigin).current
  const hasCleared = initialHasOrigin && !inputValue

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { suggestedSearchItems } = useContext(GlobalSearchStateContext)

  const inputRef = useRef<HTMLInputElement>(null)

  const onClear = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_LOCATION })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
      searchItems: [],
    })
    setInputValue('')
    onRequestClearError()
  }, [globalSearchDispatch, onRequestClearError])

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      if (value.length === 0) {
        onClear()
      } else {
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_LOCATION,
          location: value,
        })
        setInputValue(value)
        onRequestClearError()
      }
    },
    [globalSearchDispatch, onClear, onRequestClearError],
  )

  const { isLoading, isError } = useGlobalSearchTypeahead({
    search: inputValue,
    searchTypes: TRIP_PLACE_SEARCH_TYPES,
    typeaheadTypes: TRIP_PLACE_TYPEAHEAD_TYPES,
  })

  const errorMsg =
    externalErrorMsg ?? (isError ? 'Failed to fetch place results' : '')

  const onLocationClick = useCallback(
    (searchItem: App.SearchItem) => {
      onChange({
        id: searchItem.value,
        name: searchItem.format.mainText,
      })
    },
    [onChange],
  )

  useEffect(() => {
    inputRef.current?.select()
    return () => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
        searchItems: [],
      })
    }
  }, [globalSearchDispatch])

  const isEmptyResult =
    inputValue.length > 0 && suggestedSearchItems.length === 0 && !isLoading

  return (
    <>
      <ModalHeaderWrapper
        title={title}
        onCloseButtonClick={isSubmitting ? noop : onCancel}
        onBackButtonClick={isSubmitting && onBack ? noop : onBack}
      >
        <VerticalSpacer gap={8}>
          <TextInput
            inputRef={inputRef}
            placeholder="Search by city or country"
            value={inputValue}
            onChange={onInputChange}
            noValidationSpacing
            endIcon={
              inputValue.length > 0 && (
                <IconButton
                  kind="tertiary"
                  onClick={onClear}
                  aria-label="Clear"
                >
                  <LineTimesIcon />
                </IconButton>
              )
            }
          />
          {errorMsg && <MessageBanner kind="critical" description={errorMsg} />}
        </VerticalSpacer>
      </ModalHeaderWrapper>
      <ModalBody>
        <ModalContent>
          {!(isLoading || isSubmitting) &&
            suggestedSearchItems.map((item) => (
              <SearchMenuListItem
                key={item.value}
                searchItem={item}
                onClick={onLocationClick}
              />
            ))}
          {(isLoading || isSubmitting) && <LoadingIndicator inline />}
          {isEmptyResult && (
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" colour="neutral-two">
                Sorry, we couldn't find <b>{inputValue}</b>.
              </BodyText>
              <Caption variant="medium">
                Check your spelling, or try a new destination.
              </Caption>
            </VerticalSpacer>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={
          // eslint-disable-next-line @luxuryescapes/jsx-no-multiline-ternaries
          hasCleared
            ? {
                children: 'Remove departure location',
                disabled: isSubmitting,
                onClick: onRemove,
              }
            : undefined
        }
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isSubmitting,
          onClick: onCancel,
        }}
      />
    </>
  )
}

export default MobileSelectTripOrigin
